/**
 * Ajuste da listagem de ativos (AllActive) conforme alocação em boxes.
 *
 * Ativos de renda fixa alocados em boxes (objetivos/reservas) não devem aparecer
 * por completo na carteira: a porção alocada (`box_applied_percentage`) já está
 * "reservada". Usamos a porcentagem (estável) em vez do `box_applied_value` em
 * R$ (que fica defasado quando o valor do ativo muda) para decidir:
 *   - soma das porcentagens >= 1 (100%)  -> ativo não aparece na listagem;
 *   - soma das porcentagens 0.8 (80%)    -> aparece apenas o valor referente a 0.2.
 *
 * O endpoint get_position é usado em outros lugares e não deve ser alterado;
 * por isso o ajuste é feito apenas aqui, no frontend, a partir do endpoint de boxes.
 */

export interface BoxPctMap {
  /** chave: tickerSymbol normalizado (lower/trim) -> soma das porcentagens alocadas */
  byTicker: Record<string, number>;
  /** chave: id da posição / investment_id -> soma das porcentagens alocadas */
  byId: Record<string, number>;
}

export const EMPTY_BOX_PCT_MAP: BoxPctMap = { byTicker: {}, byId: {} };

function isFixedIncome(productTypeName?: string): boolean {
  const type = (productTypeName || '').toLowerCase();
  return type !== 'acao' && type !== 'fii';
}

function grossValue(item: any): number {
  // Valor bruto total do ativo (sem descontar o que está em boxes).
  return (
    Number(item?.manual_update_value) ||
    Number(item?.updateValue || 0) + Number(item?.total_investment_box_applied_value || 0)
  );
}

/**
 * Monta o mapa de porcentagens alocadas por ativo a partir da resposta do
 * endpoint de boxes (Finance/box/). Soma `box_applied_percentage` de todos os
 * boxes (objetivos + reservas) em que o ativo aparece.
 */
export function buildBoxPctMap(boxes: any[]): BoxPctMap {
  const byTicker: Record<string, number> = {};
  const byId: Record<string, number> = {};

  (boxes || []).forEach((box: any) => {
    (box?.investments || []).forEach((inv: any) => {
      const pos = inv?.position;

      let pct = Number(inv?.box_applied_percentage);
      // Fallback para registros legados sem porcentagem: deriva pela razão
      // valor aplicado / valor bruto do ativo.
      if (!pct || Number.isNaN(pct)) {
        const gross = pos ? grossValue(pos) : 0;
        pct = gross > 0 ? Number(inv?.box_applied_value || 0) / gross : 0;
      }
      if (!pct || pct <= 0) return;

      const ticker = (pos?.tickerSymbol || '').toLowerCase().trim();
      if (ticker) byTicker[ticker] = (byTicker[ticker] || 0) + pct;

      [pos?.id, inv?.investment_id].forEach((id) => {
        if (id == null) return;
        const key = String(id);
        byId[key] = (byId[key] || 0) + pct;
      });
    });
  });

  return { byTicker, byId };
}

/** Porcentagem total alocada em boxes para um ativo (0 se não houver). */
export function getBoxAppliedPercentage(item: any, map: BoxPctMap): number {
  if (!isFixedIncome(item?.productTypeName)) return 0;

  const ticker = (item?.tickerSymbol || '').toLowerCase().trim();
  if (ticker && map.byTicker[ticker] != null) return map.byTicker[ticker];

  const byId = map.byId[String(item?.id)];
  return byId != null ? byId : 0;
}

/**
 * Aplica a alocação em boxes a um item da carteira, retornando um NOVO objeto
 * (não muta o original). Itens 100% alocados recebem `alocado_total = true`
 * (filtrados na exibição); itens parciais têm o `updateValue`/`equitiesQuantity`
 * reduzidos para a porção que sobra fora dos boxes.
 */
export function applyBoxAllocation<T extends Record<string, any>>(item: T, map: BoxPctMap): T {
  const pct = getBoxAppliedPercentage(item, map);
  if (!pct || pct <= 0) return item;

  if (pct >= 0.999) {
    return { ...item, alocado_total: true, updateValue: 0, equitiesQuantity: 0 };
  }

  const gross = grossValue(item);
  const remaining = gross * (1 - pct);
  const price = Number(item.closingPrice || 0);

  return {
    ...item,
    updateValue: remaining,
    equitiesQuantity:
      price > 0 ? remaining / price : Number(item.equitiesQuantity || 0) * (1 - pct),
    alocado_total: false,
  };
}

/** Mesma regra de visibilidade usada na tabela do AllActive. */
export function isVisibleAfterBox(item: any): boolean {
  return !item?.alocado_total && (item?.updateValue > 0 || item?.equitiesQuantity > 0);
}
